import React, { useState, useEffect, useRef } from 'react';
import { X, Sparkles, Loader2, Send, CheckCircle, AlertCircle, RefreshCw } from 'lucide-react';
import { apiClient } from '../services/api';
import { trackQAPracticeStarted, trackQAPracticeCompleted } from '../utils/analytics';
import { useBodyScrollLock } from '../hooks/useBodyScrollLock';

interface Props {
  categoryId?: number;
  folderId?: number;
  cardId?: number;
  categoryName: string;
  leafCardCount: number;
  onClose: () => void;
}

type Step = 'question' | 'result' | 'complete';

interface Question {
  question: string;
  words_tested: string[];
}

interface Evaluation {
  grammar: { has_errors: boolean; errors: string | null };
  naturalness: string;
  naturalness_reason: string | null;
  native_speaker_alternative: string;
  feedback: string;
}

export const ContextualPracticeModal: React.FC<Props> = ({ categoryId, folderId, cardId, categoryName, leafCardCount, onClose }) => {
  useBodyScrollLock();
  const totalQuestions = Math.min(3, leafCardCount);

  const [step, setStep] = useState<Step>('question');
  const [question, setQuestion] = useState<Question | null>(null);
  const [answer, setAnswer] = useState('');
  const [evaluation, setEvaluation] = useState<Evaluation | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isEvaluating, setIsEvaluating] = useState(false);
  const [generateError, setGenerateError] = useState<string | null>(null);
  const [evaluateError, setEvaluateError] = useState<string | null>(null);
  const [completedCount, setCompletedCount] = useState(0);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const generateQuestion = async () => {
    setIsGenerating(true);
    setGenerateError(null);
    setQuestion(null);
    setAnswer('');
    setEvaluation(null);
    setStep('question');
    try {
      const data = cardId
        ? await apiClient.generateContextualQuestionFromCards({ card_ids: [cardId], language: 'English' })
        : await apiClient.generateContextualQuestion({
            ...(folderId ? { folder_id: folderId } : { category_id: categoryId }),
            language: 'English',
          });
      setQuestion(data);
      setTimeout(() => textareaRef.current?.focus(), 50);
    } catch {
      setGenerateError("Couldn't generate a question. Try again.");
    } finally {
      setIsGenerating(false);
    }
  };

  // Auto-generate first question on mount
  useEffect(() => { generateQuestion(); trackQAPracticeStarted(); }, []);

  const handleEvaluate = async () => {
    if (!question || !answer.trim()) return;
    setIsEvaluating(true);
    setEvaluateError(null);
    try {
      const data = await apiClient.evaluateContextualAnswer({
        question: question.question,
        words_tested: question.words_tested,
        user_answer: answer.trim(),
        language: 'English',
      });
      setEvaluation(data);
      setStep('result');
    } catch {
      setEvaluateError("Couldn't evaluate your answer. Try again.");
    } finally {
      setIsEvaluating(false);
    }
  };

  const handleNext = () => {
    const next = completedCount + 1;
    setCompletedCount(next);
    if (next >= totalQuestions) {
      trackQAPracticeCompleted(next);
      setStep('complete');
    } else {
      generateQuestion();
    }
  };

  const currentNumber = completedCount + 1;
  const isLastQuestion = currentNumber >= totalQuestions;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 sm:p-6">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg flex flex-col max-h-[90vh]">
        {/* Header */}
        <div className="flex items-center justify-between px-6 pt-5 pb-4 flex-shrink-0 border-b border-gray-100">
          <div>
            <h2 className="text-base font-semibold text-gray-900 flex items-center gap-1.5">
              <Sparkles className="h-4 w-4 text-purple-500" />
              Q&amp;A Practice
            </h2>
            <p className="text-xs text-gray-400 mt-0.5 truncate">&quot;{categoryName}&quot;</p>
          </div>
          <div className="flex items-center gap-3">
            {step !== 'complete' && (
              <span className="text-xs text-gray-400 font-medium">
                {currentNumber} / {totalQuestions}
              </span>
            )}
            <button type="button" onClick={onClose} className="p-1.5 hover:bg-gray-100 rounded-full transition-colors text-gray-400">
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-4">

          {/* Generating state */}
          {isGenerating && (
            <div className="flex flex-col items-center justify-center py-10 gap-3 text-gray-400">
              <Loader2 className="h-6 w-6 animate-spin text-purple-400" />
              <p className="text-sm">Generating question…</p>
            </div>
          )}

          {/* Generate error */}
          {generateError && !isGenerating && (
            <div className="flex flex-col items-center gap-3 py-8">
              <p className="text-sm text-red-600">{generateError}</p>
              <button
                type="button"
                onClick={generateQuestion}
                className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-purple-600 hover:bg-purple-700 rounded-lg transition-colors"
              >
                <RefreshCw className="h-4 w-4" />
                Try again
              </button>
            </div>
          )}

          {/* Question step */}
          {!isGenerating && !generateError && question && step === 'question' && (
            <>
              {/* Progress bar */}
              <div className="w-full bg-gray-100 rounded-full h-1.5">
                <div
                  className="bg-purple-400 h-1.5 rounded-full transition-all"
                  style={{ width: `${((currentNumber - 1) / totalQuestions) * 100}%` }}
                />
              </div>

              {/* Question */}
              <div className="bg-purple-50 border border-purple-100 rounded-xl px-5 py-4">
                <p className="text-sm font-medium text-gray-900 leading-relaxed">
                  {question.words_tested.length > 0
                    ? <>
                        {question.question.replace(/[.?!]+$/, '')}
                        {', using '}
                        {question.words_tested.map((w, i) => (
                          <span key={i} className="text-purple-600">
                            {i > 0 ? ', ' : ''}{w}
                          </span>
                        ))}
                        {'.'}
                      </>
                    : question.question
                  }
                </p>
              </div>

              {/* Answer textarea */}
              <textarea
                ref={textareaRef}
                value={answer}
                onChange={e => setAnswer(e.target.value)}
                onKeyDown={e => {
                  if (e.key === 'Enter' && (e.metaKey || e.ctrlKey) && answer.trim() && !isEvaluating) {
                    handleEvaluate();
                  }
                }}
                placeholder="Write a phrase or sentence…"
                rows={3}
                className="w-full px-4 py-3 border border-gray-300 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-purple-500/70 focus:border-purple-300 resize-none"
                disabled={isEvaluating}
              />

              {evaluateError && (
                <p className="text-sm text-red-600">{evaluateError}</p>
              )}

              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={generateQuestion}
                  disabled={isEvaluating}
                  className="flex-1 px-3 py-2.5 text-sm font-medium border border-gray-300 rounded-xl hover:bg-gray-50 disabled:opacity-50 transition-colors"
                >
                  New question
                </button>
                <button
                  type="button"
                  onClick={handleEvaluate}
                  disabled={!answer.trim() || isEvaluating}
                  className="flex-1 flex items-center justify-center gap-2 px-3 py-2.5 text-sm font-medium bg-gradient-to-r from-purple-500 to-indigo-500 text-white rounded-xl hover:from-purple-600 hover:to-indigo-600 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-sm"
                >
                  {isEvaluating
                    ? <><Loader2 className="h-4 w-4 animate-spin" />Evaluating…</>
                    : <><Send className="h-4 w-4" />Submit</>}
                </button>
              </div>
            </>
          )}

          {/* Result step */}
          {step === 'result' && question && evaluation && (
            <>
              {/* Progress bar */}
              <div className="w-full bg-gray-100 rounded-full h-1.5">
                <div
                  className="bg-purple-400 h-1.5 rounded-full transition-all"
                  style={{ width: `${(currentNumber / totalQuestions) * 100}%` }}
                />
              </div>

              {/* Question recap */}
              <div className="bg-gray-50 border border-gray-100 rounded-xl px-4 py-3">
                <p className="text-xs text-gray-400 mb-1">Your answer</p>
                <p className="text-sm text-gray-700 italic">&quot;{answer}&quot;</p>
              </div>

              {/* Feedback card */}
              <div className="border border-gray-200 rounded-xl divide-y divide-gray-100">
                {/* Overall feedback */}
                <div className="px-4 py-3">
                  <p className="text-sm text-gray-800 font-medium">{evaluation.feedback}</p>
                </div>

                {/* Grammar */}
                <div className="flex items-start gap-3 px-4 py-3">
                  {evaluation.grammar.has_errors
                    ? <AlertCircle className="h-4 w-4 text-amber-500 mt-0.5 flex-shrink-0" />
                    : <CheckCircle className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" />}
                  <div>
                    <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide">Grammar</p>
                    <p className="text-sm text-gray-700 mt-0.5">
                      {evaluation.grammar.has_errors ? evaluation.grammar.errors : 'No errors'}
                    </p>
                  </div>
                </div>

                {/* Naturalness */}
                <div className="flex items-start gap-3 px-4 py-3">
                  {evaluation.naturalness === 'correct'
                    ? <CheckCircle className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" />
                    : <AlertCircle className="h-4 w-4 text-amber-500 mt-0.5 flex-shrink-0" />}
                  <div>
                    <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide">Naturalness</p>
                    <p className="text-sm text-gray-700 mt-0.5 capitalize">
                      {evaluation.naturalness}
                      {evaluation.naturalness_reason && ` — ${evaluation.naturalness_reason}`}
                    </p>
                  </div>
                </div>

                {/* Native speaker alternative */}
                <div className="px-4 py-3">
                  <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1">A native speaker might say</p>
                  <p className="text-sm text-indigo-700 font-medium">&quot;{evaluation.native_speaker_alternative}&quot;</p>
                </div>
              </div>

              <button
                type="button"
                onClick={isLastQuestion ? () => { setCompletedCount(totalQuestions); setStep('complete'); } : handleNext}
                className="w-full flex items-center justify-center gap-2 px-4 py-3 text-sm font-medium bg-gradient-to-r from-purple-500 to-indigo-500 text-white rounded-xl hover:from-purple-600 hover:to-indigo-600 transition-all shadow-sm"
              >
                {isLastQuestion ? 'Finish' : 'Next question →'}
              </button>
            </>
          )}

          {/* Complete screen */}
          {step === 'complete' && (
            <div className="flex flex-col items-center justify-center py-10 text-center gap-4">
              <div className="p-4 bg-green-100 rounded-full">
                <CheckCircle className="h-8 w-8 text-green-600" />
              </div>
              <div>
                <p className="text-lg font-semibold text-gray-900">Session complete!</p>
                <p className="text-sm text-gray-500 mt-1">
                  You answered {totalQuestions} question{totalQuestions !== 1 ? 's' : ''}.
                </p>
              </div>
              <button
                type="button"
                onClick={onClose}
                className="px-6 py-2.5 text-sm font-medium bg-gray-900 text-white rounded-xl hover:bg-gray-700 transition-colors"
              >
                Done
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
